import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import pedidoVentaService, {
  PedidoVenta,
  PedidoVentaDetalle,
} from '../../../services/pedidoVentaService';
import { getApiErrorMessage } from '../../../utils/apiError';
import { formatCurrency } from '../../../utils/formatters';
import {
  ConversionLineaState,
  aplicarCantidad,
  aplicarCantidadB,
  aplicarPorcentaje,
  combinarDetallesPantalla,
  initConversionLinea,
  repartirLineaConversion,
} from '../../../utils/pedidoConversionCantidades';
import { resolverPorcConversionRenglon } from '../../../services/pedidoVentaConversionMaestros';
import productoService, { OPTIONS_PRODUCTO_OPERATIVO } from '../../../services/productoService';
import { cantidadEntregablePedido } from '../../../utils/cantidadesPedido';

type LineaEditable = ConversionLineaState & {
  key: string;
  nroItem: number;
  codigoArticulo: string;
  descripcionArticulo: string;
  um: string;
  precioUnitario: number;
  /** El reparto de pantalla no incluye espera ni cancelada; al guardar hay que sumarlas de nuevo. */
  excluyeEspera: boolean;
  detalleOriginal: PedidoVentaDetalle;
};

interface ConversionPedidoDialogProps {
  open: boolean;
  pedidoId: number;
  /** Renglones que está viendo el usuario en Editar pedido, incluidos los recién agregados. */
  detallesEnPantalla?: PedidoVentaDetalle[];
  onClose: () => void;
  /** El reparto quedó guardado. Enviar todavía no se hizo. */
  onConvertido?: () => void;
  onDone: () => void;
}

const mapLineasFromPedido = (data: PedidoVenta): LineaEditable[] =>
  (data.detalles ?? []).map((d, idx) => {
    const yaPartida = Number(d.cantidadB) > 0;
    const pct = d.porcCantB ?? d.porcSubtotalCantB;
    const state = yaPartida
      ? initConversionLinea(d.cantidad, d.cantidadB, pct)
      : repartirLineaConversion(
        cantidadEntregablePedido(d.cantidad, d.cantidadesPES, d.cantidadCanc),
        0,
        pct,
      );
    return {
      ...state,
      key: `${d.nroItem ?? idx}-${d.codigoArticulo ?? ''}`,
      nroItem: d.nroItem || idx + 1,
      codigoArticulo: String(d.codigoArticulo ?? '').trim(),
      descripcionArticulo: String(d.descripcionArticulo ?? '').trim(),
      um: String(d.um ?? '').trim(),
      precioUnitario: Number(d.precioUnitarioFinal ?? d.precioUnitario ?? 0),
      excluyeEspera: !yaPartida,
      detalleOriginal: {
        ...d,
        cantidad: state.cantidad,
        cantidadB: state.cantidadB,
        porcCantB: state.porcentaje,
        porcSubtotalCantB: state.porcentaje,
      },
    };
  });

const pedidoConLineasFloor = (pedido: PedidoVenta, lineas: LineaEditable[]): PedidoVenta => {
  const detalles: PedidoVentaDetalle[] = lineas.map((l) => {
    const espera = Math.max(0, Math.trunc(Number(l.detalleOriginal.cantidadesPES) || 0));
    const cancelada = Math.max(0, Math.trunc(Number(l.detalleOriginal.cantidadCanc) || 0));
    const cantidad = l.excluyeEspera ? l.cantidad + espera + cancelada : l.cantidad;
    return {
      ...l.detalleOriginal,
      nroItem: l.nroItem,
      codigoArticulo: l.codigoArticulo,
      descripcionArticulo: l.descripcionArticulo,
      cantidad,
      cantidadB: l.cantidadB,
      porcCantB: l.porcentaje,
      porcSubtotalCantB: l.porcentaje,
      subtotal: Number(l.detalleOriginal.precioUnitario ?? l.precioUnitario ?? 0) * cantidad,
    };
  });
  const importeTotal = detalles.reduce((acc, d) => {
    const sub = d.subtotal ?? Number(d.precioUnitario ?? 0) * Number(d.cantidad ?? 0);
    return acc + sub;
  }, 0);
  return { ...pedido, detalles, importeTotal };
};
const ConversionPedidoDialog: React.FC<ConversionPedidoDialogProps> = ({
  open,
  pedidoId,
  detallesEnPantalla,
  onClose,
  onConvertido,
  onDone,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [convirtiendo, setConvirtiendo] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [convertido, setConvertido] = useState(false);
  const [applyingMaestros, setApplyingMaestros] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pedido, setPedido] = useState<PedidoVenta | null>(null);
  const [pedidoSnapshot, setPedidoSnapshot] = useState<PedidoVenta | null>(null);
  const [lineas, setLineas] = useState<LineaEditable[]>([]);
  const [resultadoEnvio, setResultadoEnvio] = useState<{ ok: boolean; msg: string }[] | null>(null);

  const busy = convirtiendo || enviando || applyingMaestros;
  const detallesEnPantallaRef = React.useRef(detallesEnPantalla);
  detallesEnPantallaRef.current = detallesEnPantalla;

  useEffect(() => {
    if (!open || pedidoId <= 0) return;
    let cancelled = false;
    setError(null);
    setResultadoEnvio(null);
    setConvertido(false);
    setApplyingMaestros(false);
    setLoading(true);
    void (async () => {
      try {
        const data = await pedidoVentaService.getPedidoVenta(pedidoId, { sinCache: true });
        if (cancelled) return;
        const detalles = combinarDetallesPantalla(
          data.detalles ?? [],
          detallesEnPantallaRef.current,
        );
        const pedidoVista = { ...data, detalles };
        const lineasVista = mapLineasFromPedido(pedidoVista);
        setPedido(pedidoVista);
        // Snapshot del servidor, previo a aplicar % / convertir (para revertir si el envío falla)
        setPedidoSnapshot(JSON.parse(JSON.stringify(data)) as PedidoVenta);
        setLineas(lineasVista);
        // Si el pedido ya tiene Cant. conv. guardada, Enviar queda habilitado sin volver a convertir.
        setConvertido(lineasVista.some((l) => l.cantidadB > 0));
      } catch (err: unknown) {
        if (!cancelled) {
          setError(getApiErrorMessage(err) || 'No se pudo cargar el pedido para conversión.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, pedidoId]);

  const tieneCantidadB = useMemo(
    () => lineas.some((l) => l.cantidadB > 0),
    [lineas],
  );

  const totalImporteOrigen = useMemo(
    () => lineas.reduce((acc, l) => acc + l.precioUnitario * l.cantidad, 0),
    [lineas],
  );

  const updateLinea = (key: string, next: ConversionLineaState) => {
    setConvertido(false);
    setLineas((prev) => prev.map((l) => (l.key === key ? { ...l, ...next } : l)));
  };

  const restaurarPedido = async () => {
    if (!pedidoSnapshot) return;
    try {
      await pedidoVentaService.updatePedidoVenta(pedidoId, pedidoSnapshot, {
        preservarPrecios: true,
      });
      setPedido(pedidoSnapshot);
      setLineas(mapLineasFromPedido(pedidoSnapshot));
    } catch {
      enqueueSnackbar(
        'No se pudo revertir el pedido tras el error. Revisá el pedido manualmente.',
        { variant: 'warning', autoHideDuration: 6000 },
      );
    }
  };

  const handleAplicarMaestros = async () => {
    if (!pedido || lineas.length === 0) return;
    try {
      setApplyingMaestros(true);
      setConvertido(false);
      setError(null);
      // Persistí altas/bajas de Editar pedido para no trabajar sobre renglones viejos.
      const payloadPantalla = pedidoConLineasFloor(pedido, lineas);
      await pedidoVentaService.updatePedidoVenta(pedidoId, payloadPantalla, {
        preservarPrecios: true,
      });

      // % del cliente (o del proveedor): siempre pisa el del renglón (p. ej. 90 → 30).
      const nroCliente = Number(pedido.nroCliente) || 0;
      const lineasConMaestro: LineaEditable[] = [];
      for (const l of lineas) {
        let producto: unknown = null;
        const cod = String(l.codigoArticulo ?? '').trim();
        if (cod) {
          try {
            producto = await productoService.getProducto(cod, OPTIONS_PRODUCTO_OPERATIVO);
          } catch {
            /* sin producto: solo % del cliente */
          }
        }
        const porcMaestro = await resolverPorcConversionRenglon(nroCliente, producto);
        const state = aplicarPorcentaje(
          { total: l.total, cantidad: l.total, cantidadB: 0, porcentaje: 0 },
          porcMaestro,
        );
        lineasConMaestro.push({
          ...l,
          ...state,
          detalleOriginal: {
            ...l.detalleOriginal,
            cantidad: state.cantidad,
            cantidadB: state.cantidadB,
            porcCantB: state.porcentaje,
            porcSubtotalCantB: state.porcentaje,
          },
        });
      }

      const payloadMaestros = pedidoConLineasFloor(pedido, lineasConMaestro);
      await pedidoVentaService.updatePedidoVenta(pedidoId, payloadMaestros, {
        preservarPrecios: true,
      });
      setPedido(payloadMaestros);
      setPedidoSnapshot(JSON.parse(JSON.stringify(payloadMaestros)) as PedidoVenta);
      setLineas(lineasConMaestro);
      onConvertido?.();

      const conB = lineasConMaestro.some((l) => l.cantidadB > 0);
      if (conB) {
        enqueueSnackbar('Se aplicó el % Conv. del cliente o del proveedor (redondeo hacia abajo).', {
          variant: 'success',
        });
      } else {
        enqueueSnackbar(
          'Con redondeo hacia abajo no quedó Cant. conv. (p. ej. 2 uds al 30% Conv.). Revisá el % Conv. o la Cant.',
          { variant: 'warning', autoHideDuration: 6000 },
        );
      }
    } catch (err: unknown) {
      setError(
        getApiErrorMessage(err) ||
          'No se pudo aplicar el % Conv.',
      );
    } finally {
      setApplyingMaestros(false);
    }
  };

  const handleConvertir = async () => {
    if (!pedido || lineas.length === 0) return;
    try {
      setConvirtiendo(true);
      setError(null);
      const payload = pedidoConLineasFloor(pedido, lineas);
      await pedidoVentaService.updatePedidoVenta(pedidoId, payload, { preservarPrecios: true });
      setPedido(payload);
      setPedidoSnapshot(JSON.parse(JSON.stringify(payload)) as PedidoVenta);
      setConvertido(true);
      if (tieneCantidadB) {
        enqueueSnackbar('Conversión guardada. Ya podés enviar.', { variant: 'success' });
      } else {
        enqueueSnackbar(
          'Reparto restablecido: Cant. conv. en 0. El pedido vuelve sin división.',
          { variant: 'success' },
        );
      }
      onConvertido?.();
    } catch (err: unknown) {
      setConvertido(false);
      setError(getApiErrorMessage(err) || 'No se pudo convertir.');
    } finally {
      setConvirtiendo(false);
    }
  };

  const handleEnviar = async () => {
    if (!pedido || !convertido || !tieneCantidadB) return;
    try {
      setEnviando(true);
      setError(null);
      const copia = await pedidoVentaService.copiarRelacionados(pedidoId);
      const mensajes = copia.destinos.map((d) => ({
        ok: d.ok,
        msg: d.ok
          ? `${d.tenantCode || `Empresa ${d.tenantId}`}: pedido ${d.nroFormulario ?? '—'}`
          : `${d.tenantCode || `Empresa ${d.tenantId}`}: ${d.error || 'Error'}`,
      }));
      setResultadoEnvio(mensajes);
      const ok = copia.destinos.filter((d) => d.ok);
      if (ok.length > 0) {
        enqueueSnackbar(
          ok.length === 1
            ? `Pedido enviado a ${ok[0].tenantCode || 'la empresa relacionada'}.`
            : `Pedido enviado a ${ok.length} empresas.`,
          { variant: ok.length < copia.destinos.length ? 'warning' : 'success' },
        );
        onDone();
      } else {
        await restaurarPedido();
        setConvertido(true);
        enqueueSnackbar('No se pudo enviar. El pedido origen conserva la conversión guardada.', {
          variant: 'error',
        });
        setError('El envío falló en todos los destinos. La conversión guardada sigue en el pedido origen.');
      }
    } catch (err: unknown) {
      await restaurarPedido();
      setConvertido(true);
      setError(
        `${getApiErrorMessage(err) || 'No se pudo enviar.'} La conversión guardada sigue en el pedido origen.`,
      );
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="lg"
      fullWidth
      // Por encima del diálogo de detalle del pedido
      sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ fontWeight: 700 }}>Conversión</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Usá <strong>Aplicar Porcentaje</strong> para cargar el <strong>% Conv.</strong> del cliente (o del
              proveedor del artículo si el cliente no tiene). <strong>Cant.</strong> es la cantidad del
              renglón. Después podés ajustar <strong>Cant. origen</strong>, <strong>Cant. conv.</strong> y{' '}
              <strong>% Conv.</strong> a mano. <strong>Convertir</strong> guarda el reparto (también con
              Cant. conv. en 0, para restablecer sin dividir). <strong>Enviar</strong> se habilita
              después si hay Cant. conv. y manda esa porción.
            </Typography>
            {error && <Alert severity="error">{error}</Alert>}
            {resultadoEnvio && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {resultadoEnvio.map((item) => (
                  <Alert key={item.msg} severity={item.ok ? 'success' : 'error'}>
                    {item.msg}
                  </Alert>
                ))}
              </Box>
            )}
            {!resultadoEnvio && (
              <>
                <TableContainer sx={{ maxHeight: 360 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ minWidth: 180 }}>Artículo</TableCell>
                        <TableCell sx={{ width: 72 }}>UM</TableCell>
                        <TableCell align="right" sx={{ width: 88 }}>
                          <Tooltip title="Misma Cant. del renglón del pedido">
                            <span>Cant.</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ width: 140 }}>
                          <Tooltip title="Lo que queda en este pedido">
                            <span>Cant. origen</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ width: 140 }}>
                          <Tooltip title="Lo que se convierte, según el % Conv.">
                            <span>Cant. conv.</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ width: 112 }}>
                          <Tooltip title="Mismo % Conv. del renglón del pedido">
                            <span>% Conv.</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right" sx={{ width: 120, whiteSpace: 'nowrap' }}>
                          <Tooltip title="Importe de la Cant. origen">
                            <span>Imp. origen</span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {lineas.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={7} align="center">
                            Sin artículos
                          </TableCell>
                        </TableRow>
                      )}
                      {lineas.map((linea) => {
                        const importeOrigen = linea.precioUnitario * linea.cantidad;
                        return (
                          <TableRow key={linea.key}>
                            <TableCell sx={{ maxWidth: 220 }}>
                              <Typography variant="body2" fontWeight={600} noWrap>
                                {linea.codigoArticulo || '—'}
                              </Typography>
                              {linea.descripcionArticulo ? (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                  }}
                                >
                                  {linea.descripcionArticulo}
                                </Typography>
                              ) : null}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{linea.um || '—'}</Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" fontWeight={600}>
                                {linea.total}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <TextField
                                size="small"
                                type="number"
                                fullWidth
                                value={linea.cantidad}
                                disabled={busy}
                                onChange={(e) =>
                                  updateLinea(
                                    linea.key,
                                    aplicarCantidad(linea, Number(e.target.value)),
                                  )
                                }
                                inputProps={{ min: 0, max: linea.total, step: 1 }}
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                size="small"
                                type="number"
                                fullWidth
                                value={linea.cantidadB}
                                disabled={busy}
                                onChange={(e) =>
                                  updateLinea(
                                    linea.key,
                                    aplicarCantidadB(linea, Number(e.target.value)),
                                  )
                                }
                                inputProps={{ min: 0, max: linea.total, step: 1 }}
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                size="small"
                                type="number"
                                fullWidth
                                value={linea.porcentaje}
                                disabled={busy}
                                onChange={(e) =>
                                  updateLinea(
                                    linea.key,
                                    aplicarPorcentaje(linea, Number(e.target.value)),
                                  )
                                }
                                inputProps={{ min: 0, max: 100, step: 1 }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                                {formatCurrency(importeOrigen)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                {lineas.length > 0 && (
                  <Typography variant="body2" color="text.secondary" textAlign="right">
                    Total imp. origen:{' '}
                    <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                      {formatCurrency(totalImporteOrigen)}
                    </Box>
                  </Typography>
                )}

                <Alert severity="info">
                  Al enviar, el pedido se genera en el tenant, la compañía y la sucursal
                  configurados en la sucursal de venta.
                </Alert>
              </>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions
        sx={{ px: 3, py: 2, justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}
      >
        <Button onClick={onClose} disabled={busy} sx={{ textTransform: 'none' }}>
          {resultadoEnvio ? 'Cerrar' : 'Cancelar'}
        </Button>
        {!resultadoEnvio && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={() => void handleAplicarMaestros()}
              disabled={busy || loading || lineas.length === 0}
              startIcon={applyingMaestros ? <CircularProgress size={16} /> : undefined}
              sx={{ textTransform: 'none' }}
            >
              {applyingMaestros ? 'Aplicando…' : 'Aplicar Porcentaje'}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => void handleConvertir()}
              disabled={busy || loading || lineas.length === 0}
              sx={{ textTransform: 'none' }}
            >
              {convirtiendo ? 'Convirtiendo…' : 'Convertir'}
            </Button>
            <Tooltip
              title={
                !convertido
                  ? 'Primero convertí el pedido.'
                  : !tieneCantidadB
                    ? 'No hay Cant. conv. para enviar. Dejá 0 solo si querés restablecer sin dividir.'
                    : ''
              }
            >
              <span>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => void handleEnviar()}
                  disabled={busy || loading || !convertido || !tieneCantidadB}
                  sx={{ textTransform: 'none' }}
                >
                  {enviando ? 'Enviando…' : 'Enviar'}
                </Button>
              </span>
            </Tooltip>
          </Box>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ConversionPedidoDialog;
